/**
 * The two forms that turn a stranger into a remembered donor: a phone number, then the code.
 *
 * ## Why these live in their own file
 *
 * They were written inside `DonorPanel.tsx`, where the only way in was the home page's sheet.
 * `/c/<eventId>` — the code on the hall wall — is now a second host for the same two forms, and
 * the alternative to lifting them was a second phone field with its own `inputMode`, its own
 * `autoComplete="one-time-code"`, its own six-digit rule and its own "Sending…" label. Two copies
 * of a sign-in form is how one of them quietly stops accepting the SMS autofill.
 *
 * Lifted **unchanged** apart from `headingId`, exactly as `PromptView` and `ReactionBar` were
 * lifted for the event page (GH-781). Nothing about the journey moved with them: each host still
 * owns its own `busy`, its own error slot, and its own decision about what happens after the code
 * is confirmed — the sheet goes on to ask for a name, the check-in page posts an arrival. That
 * ordering is the part that differs between the two, and it is deliberately not encoded here.
 *
 * ## What these components are not
 *
 * They are not the authentication mechanism. `POST /v1/donor/sign-in` and
 * `/v1/donor/sign-in/confirm` are, and there is exactly one of those pair in this bundle
 * (`donor-session.ts`). A screen that needed a donor identity and could not use these forms would
 * be a signal to fix the forms, never to add a third way in — the product has one, and DEC-015's
 * Apple and Google slots will arrive beside it rather than instead of it.
 *
 * Neither of them stores anything. The token is minted by the caller's `confirmDonorSignIn` and
 * remembered by the caller's `rememberToken`, because remembering a device is a decision about
 * the host's journey and not about a form.
 */
import { useState, type ReactNode } from "react";

/**
 * `headingId` exists because these headings were written for a dialog.
 *
 * Inside the sheet the `<h2>` is what `aria-labelledby="donor-title"` points at, so the id is
 * load-bearing there. On a full page nothing references it and a stray duplicate-looking id is
 * dead markup, so a host that is not a dialog passes its own — or passes nothing, and the
 * attribute is simply absent.
 */
function PhoneStep({
  busy,
  onSubmit,
  headingId = "donor-title",
  titled = true,
  heading = "Your phone number",
  lede = "We text a code to confirm it is you, and remember you on this device after that.",
}: {
  busy: boolean;
  onSubmit: (phone: string) => void;
  headingId?: string;
  /** GH-883: the check-in page's "verify your mobile" step reuses this form with its own words. */
  heading?: string;
  lede?: string;
  /**
   * False when a host draws its own heading above this form — `SignInChoices`, where the phone is
   * one of two equal ways in under one "Sign in" title (GH-856).
   */
  titled?: boolean;
}) {
  const [phone, setPhone] = useState("");
  return (
    <>
      {titled && (
        <>
          <h2 id={headingId} className="donor__title">
            {heading}
          </h2>
          <p className="donor__where">{lede}</p>
        </>
      )}
      <label className="field">
        <span className="visually-hidden">Phone number</span>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(555) 555-1234"
          value={phone}
          onChange={(changed) => setPhone(changed.target.value)}
        />
      </label>
      <button
        type="button"
        className="pay"
        disabled={busy || phone.trim().length < 7}
        onClick={() => onSubmit(phone)}
      >
        {busy ? "Sending…" : "Send code"}
      </button>
    </>
  );
}

/**
 * The phone form with the check-in page's own words (GH-883): a signed-in donor proving a mobile
 * number before checking in and entering the door prize. The same form, so the same autofill.
 */
export function VerifyMobilePhoneStep({
  busy,
  onSubmit,
  headingId,
}: {
  busy: boolean;
  onSubmit: (phone: string) => void;
  headingId: string;
}) {
  return (
    <PhoneStep
      busy={busy}
      onSubmit={onSubmit}
      headingId={headingId}
      heading="Verify your mobile number"
      lede="Checking in and the door prize use a mobile number confirmed by a text, once. One entry per number."
    />
  );
}

export function CodeStep({
  hint,
  busy,
  onSubmit,
  headingId = "donor-title",
}: {
  /**
   * The code itself, and only where nothing can deliver an SMS (DEC-008). The server decides
   * whether to send it; a client that composed this itself would be printing a secret it guessed.
   */
  hint: string | null;
  busy: boolean;
  onSubmit: (code: string) => void;
  headingId?: string;
}) {
  const [code, setCode] = useState("");
  return (
    <>
      <h2 id={headingId} className="donor__title">
        Enter the code
      </h2>
      {hint && (
        <p className="notice">
          Test mode — your code is <strong>{hint}</strong>
        </p>
      )}
      <label className="field">
        <span className="visually-hidden">Verification code</span>
        <input
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          value={code}
          onChange={(changed) => setCode(changed.target.value.replace(/\D/g, ""))}
        />
      </label>
      <button
        type="button"
        className="pay"
        disabled={busy || code.length !== 6}
        onClick={() => onSubmit(code)}
      >
        {busy ? "Checking…" : "Confirm"}
      </button>
    </>
  );
}

/**
 * Google's "G", exactly as Google's sign-in branding guidelines ship it: four colours, never
 * recoloured, never on a coloured fill. The one place on a donor page where colours come from
 * outside the token set — a provider's mark is theirs, like the Apple glyph beside it.
 */
function GoogleMark() {
  return (
    <svg className="signin__mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Apple's mark, drawn rather than typed (GH-865). The private-use glyph it replaces exists only in
 * Apple's own fonts, so it was a blank box anywhere else — and a logo-only button cannot afford a
 * blank logo. `currentColor`, so it is always the label's colour: Apple's rule is a logo and title
 * that are both black or both white, never a custom colour.
 */
function AppleMark() {
  return (
    <svg className="signin__mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
      />
    </svg>
  );
}

/**
 * The sign-in door on any page (GH-856, GH-859, GH-865): Apple and Google where offered, then the
 * phone.
 *
 * ## The provider row (GH-865)
 *
 * Owner, 2026-10-03: "Make both compact, side-by-side, and give them similar treatment." Both
 * buttons are one class list — `.pay--outline`'s footprint, ink on the page with the strong
 * hairline (Lantern ADDENDUM §12) — and differ only in their mark. Apple first, then Google.
 *
 * Side by side, each button is the provider's logo alone under a "Sign in with" caption, because
 * at a phone's width no permitted title fits twice in one row, and both providers forbid the
 * short ones: Apple's custom-button titles are only "Sign in / Sign up / Continue with Apple" (or
 * a logo-only button), and Google's guidelines say "Don't use the term 'Google' by itself". A
 * logo row under "Sign in with" is the composition Apple's HIG itself illustrates for several
 * providers. The title is still in each button — visually hidden in the row, shown when the row
 * stacks (narrow, or a large text setting) and when a provider stands alone — so the accessible
 * name is always the full "Sign in with Apple" and always matches what is on screen.
 *
 * With neither provider it is the phone form under one title, and nothing else. Never a check-in
 * picker and never a list of anybody's events: signing in is about the donor.
 */
export function SignInChoices({
  busy,
  appleOffered,
  onApple,
  googleOffered = false,
  onGoogle = () => undefined,
  onPhone,
  headingId = "donor-title",
  heading = "Sign in",
  lede = "Optional. Remembers how you pay.",
  footer = null,
}: {
  busy: boolean;
  /** Configured on the server and registered for this origin, on any browser (GH-866). */
  appleOffered: boolean;
  onApple: () => void;
  /** Configured on the server and registered for this origin (GH-859). Any device. */
  googleOffered?: boolean;
  /** Called inside the tap itself: it must open Google's window before anything awaits. */
  onGoogle?: () => void;
  onPhone: (phone: string) => void;
  headingId?: string;
  /** The host's own words for why it is asking — `/c/<eventId>` signs in to check in. */
  heading?: string;
  lede?: string;
  /**
   * Under every door (GH-871b): "Have an invite?" and its field while membership is on, so the
   * code travels on whichever door is pressed. Nothing otherwise.
   */
  footer?: ReactNode;
}) {
  const count = Number(appleOffered) + Number(googleOffered);
  return (
    <>
      <h2 id={headingId} className="donor__title">
        {heading}
      </h2>
      <p className="donor__where">{lede}</p>
      {count > 0 && (
        <div className="signin__providers">
          {count === 2 && (
            <p className="signin__with" aria-hidden="true">
              Sign in with
            </p>
          )}
          <div className="signin__row" data-count={count}>
            {appleOffered && (
              <button
                type="button"
                className="pay pay--outline signin__provider signin__provider--apple"
                disabled={busy}
                onClick={onApple}
              >
                <AppleMark />
                <span className="signin__title">Sign in with Apple</span>
              </button>
            )}
            {googleOffered && (
              <button
                type="button"
                className="pay pay--outline signin__provider signin__provider--google"
                disabled={busy}
                onClick={onGoogle}
              >
                <GoogleMark />
                <span className="signin__title">Sign in with Google</span>
              </button>
            )}
          </div>
          <p className="signin__or" aria-hidden="true">
            or
          </p>
        </div>
      )}
      <PhoneStep busy={busy} onSubmit={onPhone} headingId={headingId} titled={false} />
      {footer}
    </>
  );
}
